import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateEvidencePairDto,
  Evidence,
  EvidencePair,
  MarkupAnnotation,
  PhotoTimeline,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { evidenceKeys } from './evidence-api';
import { useMutationToast } from './mutation-toast';

export const photoTimelineKeys = {
  forFile: (fileId: string) => ['photo-timeline', fileId] as const,
};

export async function getPhotoTimeline(fileId: string): Promise<PhotoTimeline> {
  const { data } = await api.get<PhotoTimeline>(`/files/${fileId}/photo-timeline`);
  return data;
}

export async function createMarkup(
  evidenceId: string,
  annotations: MarkupAnnotation[],
  note?: string,
): Promise<Evidence> {
  const { data } = await api.post<Evidence>(`/files/evidence/${evidenceId}/markup`, {
    annotations: JSON.stringify(annotations),
    ...(note?.trim() ? { note: note.trim() } : {}),
  });
  return data;
}

export async function createPair(
  fileId: string,
  body: CreateEvidencePairDto,
): Promise<EvidencePair> {
  const { data } = await api.post<EvidencePair>(`/files/${fileId}/evidence-pairs`, body);
  return data;
}

export async function removePair(fileId: string, pairId: string): Promise<{ deleted: true }> {
  const { data } = await api.delete<{ deleted: true }>(`/files/${fileId}/evidence-pairs/${pairId}`);
  return data;
}

export function usePhotoTimeline(fileId: string | undefined) {
  return useQuery({
    queryKey: photoTimelineKeys.forFile(fileId ?? ''),
    queryFn: () => getPhotoTimeline(fileId as string),
    enabled: !!fileId,
  });
}

function useInvalidate(fileId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: photoTimelineKeys.forFile(fileId) });
    qc.invalidateQueries({ queryKey: evidenceKeys.forFile(fileId) });
  };
}

export function useCreateMarkup(fileId: string) {
  const invalidate = useInvalidate(fileId);
  const toast = useMutationToast();
  return useMutation({
    mutationFn: ({
      evidenceId,
      annotations,
      note,
    }: {
      evidenceId: string;
      annotations: MarkupAnnotation[];
      note?: string;
    }) => createMarkup(evidenceId, annotations, note),
    onSuccess: () => {
      invalidate();
      toast.success('gov.evidenceMarkup.markupSaved');
    },
    onError: (err) => toast.error(err, 'gov.evidenceMarkup.markupError'),
  });
}

export function useCreatePair(fileId: string) {
  const invalidate = useInvalidate(fileId);
  const toast = useMutationToast();
  return useMutation({
    mutationFn: (body: CreateEvidencePairDto) => createPair(fileId, body),
    onSuccess: () => {
      invalidate();
      toast.success('gov.evidenceMarkup.pairSaved');
    },
    onError: (err) => toast.error(err, 'gov.evidenceMarkup.pairError'),
  });
}

export function useRemovePair(fileId: string) {
  const invalidate = useInvalidate(fileId);
  const toast = useMutationToast();
  return useMutation({
    mutationFn: (pairId: string) => removePair(fileId, pairId),
    onSuccess: () => {
      invalidate();
      toast.success('gov.evidenceMarkup.pairRemoved');
    },
    onError: (err) => toast.error(err, 'gov.evidenceMarkup.pairError'),
  });
}
